import type { PaginatedResult } from '@api/resources/resource';
import { useQueryClient } from '@tanstack/vue-query';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import type { Partner, Task, TaskCollection } from '@stuut/api/models';
import { SubCollectionKeyMap } from '@stuut/api/models';
import { TaskQueryParams } from '@stuut/api/query';

import { stuut } from '@/api';
import { useConfetti } from '@/composables/useConfetti';
import { TASK_REPLACE_QPS } from '@/constants/constants';

interface CollectionState {
  [key: string]: any;
  collections?: any[];
  currentCollectionTasks?: any[];
  currentCollectionTasksTotal?: number;
  groupByModeId?: string;
  groupByModeOptions?: any[];
  groupViewModes?: any[];
  selectedCollection?: any;
  selectedCollectionId?: string;
  selectedGroupViewMode?: string;
  selectedGroupViewType?: any;
  selectedGroupViewTypeOption?: any;
  selectedSubCollection?: any;
  selectedSubCollectionId?: string;
  selectedTaskId?: string;
  subCollections?: any[];
}

interface OpenTaskParams {
  task?: Task;
  taskId?: string;
  taskView?: 'stack' | 'list' | 'individual';
}

const groupValueType = {
  call_analysis: 'string',
  classification: 'array',
  disable_contact: 'string',
  expected_action: 'array',
  partner: 'string',
  payment_promise: 'string',
  status: 'array',
} as const;

export const useTaskCollectionStore = defineStore('taskCollections', () => {
  const router = useRouter();
  const route = useRoute();
  const queryClient = useQueryClient();
  const showConfetti = ref(false);
  const isTaskDrawerOpen = ref(false);
  const isLoadingTask = ref(false);
  const isInitNavLoaded = ref(false);
  const isInitSubNavLoaded = ref(false);
  const isLoadingGroupByModeOptions = ref(false);
  const isLoadingCollectionTasks = ref(false);
  const isFetchingCollectionTasks = ref(false);

  // State
  const groupViewModes = ref<TaskCollection[]>([]);
  const selectedGroupViewMode = ref<string | undefined>(undefined);
  const groupByModeOptions = ref<Record<string, any>[]>([]);
  const groupByModeId = ref<string | undefined>(undefined);
  const collections = ref<TaskCollection[]>([]);
  const selectedCollectionId = ref<string | undefined>(undefined);
  const subCollections = ref<TaskCollection[]>([]);
  const selectedSubCollectionId = ref<string | undefined>(undefined);
  const currentCollectionTasks = ref<Task[]>([]);
  const currentCollectionTasksTotal = ref<number | undefined>(undefined);
  const currentTask = ref<Task | undefined>(undefined);
  const selectedTaskId = ref<string | undefined>(undefined);
  const selectedPartnerId = ref<string | undefined>(undefined);
  const sharedTaskQueryParams = ref<TaskQueryParams | undefined>(undefined);
  const partners = ref<Record<string, Partner>>({});
  const listPartnerFilter = ref<string | undefined>(undefined);
  const { triggerConfetti } = useConfetti();
  const navigationDirection = ref<'forward' | 'backward' | undefined>(undefined);

  const showAfterCompleteOverlay = ref(false);

  // Computed Properties
  const selectedTaskIndex = computed(() => {
    return currentCollectionTasks.value?.findIndex?.((task) => task.id === currentTask.value?.id);
  });

  const selectedCollectionIndex = computed(() => {
    return collections.value?.findIndex(
      (collection) => collection.id === selectedCollectionId.value,
    );
  });

  const selectedSubCollectionIndex = computed(() => {
    return subCollections.value?.findIndex(
      (collection) => collection.id === selectedSubCollectionId.value,
    );
  });

  const nextCollectionIndex = computed(() => {
    const nextIndex = (selectedCollectionIndex.value ?? -1) + 1;

    if (nextIndex <= (collections.value?.length ?? 0) - 1) return nextIndex;

    return 0;
  });

  const nextCollection = computed(() => {
    return collections.value?.[nextCollectionIndex.value];
  });

  const nextSubCollectionIndex = computed(() => {
    if (!subCollections.value?.length) return null;

    const nextIndex = (selectedSubCollectionIndex.value ?? -1) + 1;

    if (nextIndex <= (subCollections.value?.length ?? 0) - 1) return nextIndex;

    return 0;
  });

  const nextSubCollection = computed(() => {
    if (!subCollections.value?.length) return null;
    const index = nextSubCollectionIndex.value;
    if (index === null) return null;

    return subCollections.value?.[index];
  });

  const previousSubCollection = computed(() => {
    if (!subCollections.value?.length) return null;
    const index = previousSubCollectionIndex.value;
    if (index === null) return null;

    return subCollections.value?.[index];
  });

  const previousSubCollectionIndex = computed(() => {
    if (!subCollections.value?.length) return null;

    const previousIndex = (selectedSubCollectionIndex.value ?? 0) - 1;

    if (previousIndex >= 0) return previousIndex;

    return -1;
  });

  const previousCollection = computed(() => {
    return collections.value?.[previousCollectionIndex.value];
  });

  const previousCollectionIndex = computed(() => {
    const previousIndex = (selectedCollectionIndex.value ?? 0) - 1;

    if (previousIndex >= 0) return previousIndex;

    return collections.value?.length - 1;
  });

  const nextTask = computed(() => {
    return currentCollectionTasks.value?.[nextTaskIndex.value];
  });

  const nextTaskIndex = computed(() => {
    const nextIndex = selectedTaskIndex.value + 1;

    if (nextIndex <= currentCollectionTasks.value?.length - 1) {
      return nextIndex;
    }

    return 0;
  });

  const previousTask = computed(() => {
    return currentCollectionTasks.value?.[previousTaskIndex.value];
  });

  const previousTaskIndex = computed(() => {
    const previousIndex = selectedTaskIndex.value - 1;

    if (previousIndex >= 0) return previousIndex;

    return currentCollectionTasks.value?.length - 1;
  });

  const isTaskListMode = computed(() => route.query?.task_display_mode === 'list');

  const isTaskStackedMode = computed(
    () =>
      route.query?.task_display_mode === undefined || route.query?.task_display_mode === 'stacked',
  );

  const currentModalTaskType = computed(() => {
    if (getIsIndividualView()) {
      return currentTask.value?.type as string;
    }

    if (route.query.collection_type === 'task_type') {
      return route.query.collection_id as string;
    }

    if (route.query.group_view_mode === 'task_type') {
      return route.query.group_view_id as string;
    }

    return undefined;
  });

  const currentModalClassification = computed(() => {
    if (getIsIndividualView()) {
      return currentTask.value?.classifications[0] as string;
    }

    if (route.query.collection_type === 'task_type') {
      return route.query.sub_collection_id as string;
    }

    if (route.query.group_view_mode === 'task_type') {
      return route.query.collection_id as string;
    }

    return undefined;
  });

  const currentTaskTypeCollections = computed(() => {
    if (route.query.collection_type === 'task_type') {
      return collections.value;
    }

    if (route.query.group_view_mode === 'task_type') {
      return [selectedGroupViewTypeOption.value];
    }

    return undefined;
  });

  // Additional computed properties that were missing
  const getById = computed(() => {
    return (taskId: string) =>
      currentCollectionTasks.value?.find((task) => task.id === taskId) as Task | undefined;
  });

  const partnerGroupId = computed(() => {
    // Note: 'd' property seems to be missing - may need to add this
    return selectedCollectionId.value;
  });

  const selectedCollection = computed(() => {
    return collections.value?.find((group) => group.id === selectedCollectionId.value);
  });

  const selectedSubCollection = computed(() => {
    return subCollections.value?.find((group) => group.id === selectedSubCollectionId.value);
  });

  const isClassificationGroup = computed(() => {
    return selectedCollection.value?.collection_type === 'classification';
  });

  const isClassificationCollection = computed(() => {
    return selectedCollection.value?.collection_type === 'classification';
  });

  const classifications = computed(() => {
    if (isClassificationGroup.value) {
      return selectedCollectionId.value;
    }

    if (isClassificationCollection.value) {
      return selectedCollectionId.value;
    }

    return null;
  });

  const currentGroupViewMode = computed(() => {
    return groupViewModes.value?.find((group) => group.id === selectedGroupViewMode.value);
  });

  const selectedGroupViewTypeOption = computed(() => {
    return groupByModeOptions.value?.find((group) => group.id === groupByModeId.value);
  });

  const groupTaskCount = computed(() => {
    return selectedGroupViewTypeOption.value?.task_count;
  });

  const currentPartner = computed(() => {
    if (!selectedPartnerId.value) return undefined;

    const partner = partners.value?.[selectedPartnerId.value];

    return partner ? (partner as Partner) : undefined;
  });

  const getTaskParentCollectionType = computed(() => {
    return selectedSubCollectionId.value ? 'subCollection' : 'collection';
  });

  const getTaskParentCollection = computed(() => {
    return selectedSubCollection.value || selectedCollection.value;
  });

  const taskDisplayMode = computed(() => route.query?.task_display_mode as string);

  const getIsGroupView = () => {
    return (
      route.query?.group_view_mode === 'task_type' || route.query?.group_view_mode === 'partner'
    );
  };

  const getIsIndividualView = () => {
    return !getIsGroupView();
  };

  const isTaskTypeGroupView = computed(() => route.query?.group_view_mode === 'task_type');

  const isPartnerGroupView = computed(() => route.query?.group_view_mode === 'partner');

  const isGroupView = computed(() => getIsGroupView());

  const isIndividualTaskView = computed(() => getIsIndividualView());

  const isBasicTask = computed(() => isTaskStackedMode.value);

  // const hasNoTaskPagin = computed(
  //   () =>
  //     !currentCollectionTasks.value ||
  //     currentCollectionTasks.value?.length <= 1,
  // );

  const hasNoCollectionPagin = computed(
    () => !collections.value || collections?.value?.length <= 1,
  );

  const hasNoSubCollectionPagin = computed(
    () => !subCollections.value || subCollections.value?.length <= 1,
  );

  const hasNoSubOrCollectionPagin = computed(
    () => hasNoCollectionPagin.value && hasNoSubCollectionPagin.value,
  );

  // Actions
  const setup = () => {
    return setGroupViewModes();
  };

  const setGroupViewModes = async () => {
    if (groupViewModes.value?.length) return groupViewModes.value;

    groupViewModes.value = await fetchGroupViewModes();

    return groupViewModes.value;
  };

  const fetchGroupViewModes = async (): Promise<TaskCollection[]> => {
    return stuut.taskCollections.list({
      query: {
        status: ['open'],
      },
    } as any);
  };

  const setGroupViewModeOptions = async (
    groupViewMode: string,
    args?: Record<string, any>,
    pagination?: boolean,
  ) => {
    isLoadingGroupByModeOptions.value = true;

    try {
      const response = await fetchCollectByModeOptions(groupViewMode, args, pagination);

      if (pagination) {
        groupByModeOptions.value = (response as PaginatedResult<TaskCollection>).data;
      } else {
        groupByModeOptions.value = response as TaskCollection[];
      }

      return response;
    } catch (error: unknown) {
      if (
        error &&
        typeof error === 'object' &&
        'statusCode' in error &&
        (error as any).statusCode === 422
      ) {
        console.warn(
          'Invalid filter parameters for task collection grouping:',
          (error as any).clientMessage,
        );
        groupByModeOptions.value = [];
      } else {
        throw error;
      }
    } finally {
      isLoadingGroupByModeOptions.value = false;
    }
  };

  const fetchCollectByModeOptions = async (
    groupViewMode?: string,
    args?: Record<string, any>,
    pagination?: boolean,
  ) => {
    const collectBy = groupViewMode || selectedGroupViewMode.value;

    const queryArgs: Record<string, any> = {
      status: ['open'],
      ...(args?.query || {}),
    };

    if (route.query.assignee) {
      queryArgs.assignee = Array.isArray(route.query.assignee)
        ? route.query.assignee[0]
        : route.query.assignee;
    }

    if (pagination) {
      return stuut.taskCollections.list(
        {
          params: {
            group_by: collectBy!,
            ...queryArgs,
          },
        },
        true,
      );
    }

    return stuut.taskCollections.list({
      params: {
        group_by: collectBy,
        ...queryArgs,
      },
    });
  };

  const setCollections = async (collectBy?: string) => {
    collections.value = (await fetchCollections(collectBy || '')) as TaskCollection[];

    isInitNavLoaded.value = true;

    return collections.value;
  };

  const fetchCollections = async (collectBy: string) => {
    const assigneeFilter = route.query?.assignee
      ? {
          assignee: Array.isArray(route.query.assignee)
            ? route.query.assignee[0]
            : route.query.assignee,
        }
      : {};

    const response = await stuut.taskCollections.list(
      {
        params: {
          group_by: collectBy,
          [route.query?.group_view_mode as string]:
            groupValueType[route.query?.group_view_mode as keyof typeof groupValueType] === 'array'
              ? [route.query?.group_view_id]
              : route.query?.group_view_id,
          status: ['open'],
          ...assigneeFilter,
        },
      },
      true,
    );

    return response.data;
  };

  const setSubCollections = async (collectionId?: string, collectionType?: string) => {
    subCollections.value = (await fetchSubCollections(
      collectionId,
      collectionType,
    )) as TaskCollection[];

    isInitSubNavLoaded.value = true;

    return subCollections.value;
  };

  const fetchSubCollections = async (id?: string, type?: string) => {
    const collectionIdToUse = id || selectedCollectionId.value;
    if (!collectionIdToUse) return [];

    const params: Record<string, any> = {
      group_by: SubCollectionKeyMap[collectionIdToUse as keyof typeof SubCollectionKeyMap],
      status: ['open'],
    };

    if (route.query.group_view_mode) {
      params[route.query.group_view_mode as string] =
        groupValueType[route.query.group_view_mode as keyof typeof groupValueType] === 'array'
          ? [route.query.group_view_id]
          : route.query.group_view_id;
    }

    if (route.query.collection_type) {
      params[route.query.collection_type as string] =
        groupValueType[(type || route.query.collection_type) as keyof typeof groupValueType] ===
        'array'
          ? [type || route.query.collection_id]
          : type || route.query.collection_id;
    }

    if (route.query.assignee) {
      params.assignee = Array.isArray(route.query.assignee)
        ? route.query.assignee[0]
        : route.query.assignee;
    }

    const response = await stuut.taskCollections.list({
      params,
    });

    return response;
  };

  const setCollectionTasks = async (queryParams) => {
    try {
      isLoadingCollectionTasks.value = true;

      const response = await fetchCollectionTasks(queryParams);

      currentCollectionTasks.value = response?.data || [];
      currentCollectionTasksTotal.value = response?.total_records;
    } catch (err: any) {
      console.error(err.clientMessage);
    } finally {
      isLoadingCollectionTasks.value = false;
    }
  };

  const fetchCollectionTasks = async (queryParams) => {
    const response = await requestFromQps(queryParams);

    return response;
  };

  const closeTaskDrawer = () => {
    clearValues();

    router.replace({
      query: {
        ...route.query,
        ...TASK_REPLACE_QPS,
        group_view_mode: route.query.group_view_mode,
      },
    });

    updateSharedQueryParams({ page: 0 });

    // updateSharedQueryParams({
    //   task_display_mode: undefined,
    //   group_view_id: undefined,
    //   collection_type: undefined,
    //   collection_id: undefined,
    //   sub_collection_type: undefined,
    //   sub_collection_id: undefined,
    //   task_drawer_id: undefined,
    //   page: 0,
    // });
  };

  const buildCollectionState = async () => {
    const current = {} as any;
    const state = {} as CollectionState;

    state.groupViewModes = await setGroupViewModes();

    if (route.query.group_view_mode) {
      state.selectedGroupViewType = route.query.group_view_mode as string;
      // TODO delete/refactor
      state.selectedGroupViewMode = route.query.group_view_mode as string;

      current.groupViewMode = state.groupViewModes?.find(
        (opt) => opt.collection_type === state.selectedGroupViewMode,
      );
    }

    const isGroupMode = getIsGroupView();

    if (isGroupMode) {
      const groupOptions = await setGroupViewModeOptions(route.query.group_view_mode as string, {
        query: { status: ['open'] },
      });

      state.groupByModeOptions = Array.isArray(groupOptions) ? groupOptions : groupOptions.data;
    }

    if (route.query.group_view_id) {
      state.groupByModeId =
        (route.query.group_view_id as string) || state.selectedGroupViewType?.id;

      state.selectedGroupViewTypeOption = state.groupByModeOptions?.find(
        (opt) => opt.id === state.groupByModeId,
      );

      if (!state.selectedGroupViewTypeOption) {
        const collectByOption = await fetchCollectByModeOptions('partner', {
          query: {
            partner: state.groupByModeId,
          },
        });

        state.selectedGroupViewTypeOption = collectByOption;
        state.groupByModeOptions?.push(collectByOption);
      }
    }

    if (isGroupMode) {
      if (!state.selectedGroupViewTypeOption) return;

      state.selectedCollectionType =
        route?.query?.collection_type ||
        state.selectedGroupViewTypeOption?.subcollections?.[0] ||
        state?.selectedGroupViewMode === 'partner'
          ? 'task_type'
          : 'classification';

      state.collections = await setCollections(state.selectedCollectionType);

      state.selectedCollectionId = route.query.collection_id
        ? (route.query.collection_id as string)
        : state.collections?.[0]?.id;

      state.selectedCollection = state.collections?.find(
        (collection) => collection.id === state.selectedCollectionId,
      );

      if (state.selectedCollection?.subcollections?.length) {
        state.subCollections = await setSubCollections(
          state.selectedCollectionId,
          state.selectedCollectionId,
        );

        if (route.query.sub_collection_id) {
          state.selectedSubCollectionId = route.query.sub_collection_id as string;
          // } else if () {
          // state['selectedSubCollectionId'] = state['subCollections']?.[0]?.id;
        }

        if (state.selectedSubCollectionId) {
          state.selectedSubCollection = state.subCollections?.find(
            (g) => g.id === state.selectedSubCollectionId,
          );
        }
      }
    }

    const newQueryParams: Record<string, any> = {
      collection_id: state.selectedCollectionId,
      collection_type: state.selectedCollectionType,

      group_view_id: state.groupByModeId,
      group_view_mode: state.selectedGroupViewMode,

      sub_collection_id: state.selectedSubCollectionId,
      sub_collection_type: state.selectedSubCollection?.collection_type,
    };

    const taskResponse = await requestFromQps(newQueryParams);

    state.currentCollectionTasks = taskResponse?.data;
    state.currentCollectionTasksTotal = taskResponse?.total_records;

    if (route.query.task_drawer_id) {
      state.selectedTaskId = route.query.task_drawer_id as string;

      const taskRecord = state.currentCollectionTasks?.find(
        (t) => t.id === route.query.task_drawer_id,
      ) as Task;

      if (taskRecord?.data) state.currentTask = taskRecord;
    } else {
      const firstTask = state.currentCollectionTasks?.[0];

      state.selectedTaskId = firstTask?.id;

      if (firstTask?.data) state.currentTask = firstTask;
    }

    newQueryParams.task_drawer_id = state.selectedTaskId;

    if (!state.currentTask?.data) {
      try {
        const task = await peekLoadTask(state.selectedTaskId);

        if (task?.id) setCurrentTask(task as Task);

        state.currentTask = task;
      } catch (err: any) {
        console.error(err.clientMessage);
      }
    }

    return {
      queryParams: {
        ...newQueryParams,
      },
      selectedCollection: state.selectedCollection,
      selectedGroupViewType: state.selectedGroupViewType,
      selectedGroupViewTypeOption: state.selectedGroupViewTypeOption,
      selectedSubCollection: state.selectedSubCollection,
      state: {
        collections: state.collections,
        currentCollectionTasks: state.currentCollectionTasks,
        currentCollectionTasksTotal: state.currentCollectionTasksTotal,
        currentTask: state.currentTask,
        groupByModeId: state.groupByModeId,
        groupByModeOptions: state.groupByModeOptions,
        groupViewModes: state.groupViewModes,
        selectedCollectionId: state.selectedCollectionId,
        selectedCollectionType: state.selectedCollectionType,
        selectedGroupViewMode: state.selectedGroupViewMode,
        selectedSubCollectionId: state.selectedSubCollectionId,
        selectedSubCollectionType: state.selectedSubCollectionType,
        selectedTaskId: state.selectedTaskId,
        subCollections: state.subCollections,
      },
    };
  };

  const clearValues = () => {
    groupByModeId.value = undefined;

    collections.value = [];
    selectedCollectionId.value = undefined;

    subCollections.value = [];
    selectedSubCollectionId.value = undefined;

    currentCollectionTasks.value = [];
    currentCollectionTasksTotal.value = undefined;

    selectedPartnerId.value = undefined;
    selectedTaskId.value = undefined;
    currentTask.value = undefined;

    showConfetti.value = false;
    isTaskDrawerOpen.value = false;
    isLoadingTask.value = false;
    listPartnerFilter.value = undefined;

    isInitNavLoaded.value = false;
    isInitSubNavLoaded.value = false;
    isLoadingCollectionTasks.value = false;
    isFetchingCollectionTasks.value = false;

    showAfterCompleteOverlay.value = false;

    updateSharedQueryParams({ page: 0 });
  };

  const clearGroupByModeOptions = () => {
    groupByModeOptions.value = [];
  };

  const setListPartnerFilter = (id?: string) => {
    listPartnerFilter.value = id;
  };

  const initializeSharedQueryParams = (options: Record<string, any> = {}) => {
    const defaults = {
      is_automated: undefined,
      page: 0,
      pageSize: 50,
      ...(options as any)?.defaults,
    };

    sharedTaskQueryParams.value = TaskQueryParams.init({
      defaults,
      json: route.query,
    });

    return sharedTaskQueryParams.value;
  };

  const updateSharedQueryParams = (updates: Record<string, any>) => {
    sharedTaskQueryParams.value?.update?.(updates);
    return sharedTaskQueryParams.value;
  };

  const buildRequestFromQPs = async (route: any = {}) => {
    const localGroupValueType = {
      classification: 'array',
      classifications_any: 'array',
      classifications_eq: 'array',
      expected_action: 'array',
      partner: 'string',
      status: 'array',
    } as const;

    const filterKeyMap: Record<string, string> = {
      classification: 'classifications_eq',
    };

    const filterKeyTransform = (queryName: string) => {
      return filterKeyMap[queryName] || queryName;
    };

    const query: Record<string, any> = {
      ...sharedTaskQueryParams.value?.serialize({
        forApiRequest: true,
      }),
    };

    if (route?.group_view_mode && route?.group_view_id) {
      query[filterKeyTransform(route.group_view_mode)] =
        localGroupValueType[route.group_view_mode as keyof typeof localGroupValueType] === 'array'
          ? [route?.group_view_id]
          : route?.group_view_id;
    }

    if (route?.collection_type && route?.group_view_id) {
      query[filterKeyTransform(route?.collection_type)] =
        localGroupValueType[route?.collection_type as keyof typeof localGroupValueType] === 'array'
          ? [route?.collection_id]
          : route?.collection_id;
    }

    if (route?.sub_collection_type && route?.sub_collection_id) {
      query[filterKeyTransform(route.sub_collection_type)] =
        localGroupValueType[route?.sub_collection_type as keyof typeof localGroupValueType] ===
        'array'
          ? [route?.sub_collection_id]
          : route?.sub_collection_id;
    }

    if (route?.assignee) {
      query.assignee = Array.isArray(route.assignee) ? route.assignee[0] : route.assignee;
    }

    Object.keys(query).forEach((key) => {
      if (query[key] === null || query[key] === undefined || query[key] === '') {
        delete query[key];
      }
    });

    return query;
  };

  const requestFromQps = async (queryParams: any) => {
    isFetchingCollectionTasks.value = true;

    try {
      const query = await buildRequestFromQPs(queryParams);

      if (Array.isArray(query.assignee)) query.assignee = query.assignee[0];

      const response = await stuut.tasks.list(
        {
          params: {
            partner: isTaskTypeGroupView.value ? listPartnerFilter.value : undefined,
            ...query,
            status: ['open'],
          },
        },
        true,
      );

      return response;
    } catch (err: any) {
      console.error(err.clientMessage);
    } finally {
      isFetchingCollectionTasks.value = false;
    }
  };

  const updateFromQps = async (queryParams: any) => {
    const response = await requestFromQps(queryParams);

    currentCollectionTasks.value = response?.data || [];
    currentCollectionTasksTotal.value = response?.total_records;

    return response;
  };

  const setCurrentTask = async (task: Task) => {
    selectedTaskId.value = task?.id;

    updateCollectionTasks(task);

    currentTask.value = task;

    return currentTask.value;
  };

  const updateCollectionTasks = async (task: Task) => {
    const index = currentCollectionTasks.value?.findIndex((t) => t.id === task?.id);

    if (index > -1) {
      const updatedTasks = [...currentCollectionTasks.value];

      updatedTasks[index] = task;

      currentCollectionTasks.value = updatedTasks;
    }
  };

  const gotoPreviousTask = async (router: any, route: any) => {
    // Set navigation direction for animations
    navigationDirection.value = 'backward';

    if ((previousTaskIndex.value ?? -1) < (selectedTaskIndex.value ?? -1)) {
      return router.push({
        query: {
          ...route.query,
          task_drawer_id: previousTask.value?.id,
        },
      });
    }

    if (previousSubCollectionIndex.value !== null) {
      if (previousSubCollectionIndex.value >= 0 && previousSubCollectionIndex.value !== -1) {
        return router.push({
          query: {
            ...route.query,
            sub_collection_id: previousSubCollection.value?.id,
            task_drawer_id: undefined,
          },
        });
      }
    }

    if (previousCollectionIndex.value === (collections.value?.length ?? 0) - 1) {
      return router.push({
        query: {
          ...route.query,
          collection_id: collections.value?.[collections.value?.length - 1]?.id,
          sub_collection_id: undefined,
          task_drawer_id: undefined,
        },
      });
    }

    if ((previousCollectionIndex.value ?? -1) >= 0) {
      return router.push({
        query: {
          ...route.query,
          collection_id: previousCollection.value?.id,
          sub_collection_id: undefined,
          task_drawer_id: undefined,
        },
      });
    }

    return router.push({
      query: {
        ...route.query,
        task_drawer_id: previousTask.value?.id,
      },
    });
  };

  const gotoNextTask = async () => {
    // Set navigation direction for animations
    navigationDirection.value = 'forward';

    if (nextTaskIndex.value) {
      return router.push({
        query: {
          ...route.query,
          task_drawer_id: nextTask.value?.id,
        },
      });
    }

    if (nextTaskIndex.value === 0) {
      if (currentCollectionTasks.value?.length > 1 && hasNoSubOrCollectionPagin.value) {
        return router.push({
          query: {
            ...route.query,
            task_drawer_id: nextTask.value?.id,
          },
        });
      }

      if (nextSubCollectionIndex.value !== null && nextSubCollectionIndex.value) {
        return router.push({
          query: {
            ...route.query,
            sub_collection_id: nextSubCollection.value?.id,
            task_drawer_id: undefined,
          },
        });
      }

      if (nextCollectionIndex.value > 0) {
        return router.push({
          query: {
            ...route.query,
            collection_id: nextCollection.value?.id,
            sub_collection_id: undefined,
            task_drawer_id: undefined,
          },
        });
      } else if (nextCollectionIndex.value === 0) {
        return router.push({
          query: {
            ...route.query,
            collection_id: collections.value?.[0]?.id,
            task_drawer_id: undefined,
          },
        });
      }

      return router.push({
        query: {
          ...route.query,
          task_drawer_id: nextTask.value?.id,
        },
      });
    }
  };

  const openAfterCompleteOverlay = () => {
    showAfterCompleteOverlay.value = true;
  };

  const closeAfterCompleteOverlay = () => {
    showAfterCompleteOverlay.value = false;
  };

  const handleConfirmAfterComplete = () => {
    closeAfterCompleteOverlay();

    afterCompleteTask();
    refreshAllTaskTables();
  };

  const allTasksCompleted = () => {
    return currentCollectionTasks.value?.filter((t) => t.isOpen)?.length === 0;
  };

  const launchConfetti = () => {
    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;

    triggerConfetti(centerX, centerY);

    currentTask.value = undefined;
    closeTaskDrawer();

    return setGroupViewModeOptions(route?.query?.group_view_mode as string);
  };

  const afterCompleteTask = async () => {
    if (allTasksCompleted() || isPartnerGroupComplete() || isTaskTypeGroupComplete()) {
      return launchConfetti();
    }

    return gotoNextTask();
  };

  const noValidNextSubCollections = computed(
    () =>
      (selectedCollection.value && !selectedCollection.value?.showSubCollections) ||
      nextSubCollectionIndex.value === null ||
      (nextSubCollectionIndex.value === 0 && subCollections.value?.length <= 1),
  );

  const noValidNextCollections = computed(
    () =>
      nextCollectionIndex.value === null ||
      (nextCollectionIndex.value === 0 && collections.value?.length <= 1),
  );

  const noValidNextTask = computed(
    () =>
      (!nextTaskIndex.value || nextTaskIndex.value <= 0) &&
      currentCollectionTasks.value?.filter((t) => t.isOpen).length <= 1,
  );

  const isTaskTypeGroupComplete = () => {
    return isTaskTypeGroupView.value && noValidNextTask.value && noValidNextCollections.value;
  };

  const isPartnerGroupComplete = () => {
    return (
      isPartnerGroupView.value &&
      noValidNextTask.value &&
      noValidNextSubCollections.value &&
      noValidNextCollections.value
    );
  };

  const peekTask = (id: string) => {
    return currentCollectionTasks.value?.find((task) => task?.id === id);
  };

  const fetchTask = (id: string) => {
    return stuut.tasks.retrieve(id);
  };

  // const updateTask = (id: string) => {
  //   return stuut.tasks.retrieve(id, currentTask.value);
  // };

  const peekFindTask = (id?: string) => {
    if (!id) return;

    const record = peekTask(id);
    if (record) return record;

    return fetchTask(id);
  };

  const peekLoadTask = async (id?: string) => {
    if (!id) return;

    const record = peekTask(id) as Task;
    if (record && record.data) return record;

    const response = await fetchTask(id);

    if (record && response) {
      const taskList = [...(currentCollectionTasks.value || [])];
      const taskIndex = taskList?.findIndex((t) => t.id === id);

      if (taskIndex !== -1) {
        taskList[taskIndex] = response;
      } else {
        taskList.push(response);
      }

      currentCollectionTasks.value = taskList;
    }

    return response;
  };

  const toggleTaskView = () => {
    router.push({
      query: {
        ...(route.query || {}),
        task_display_mode: route.query?.task_display_mode === 'list' ? 'stacked' : 'list',
      },
    });
  };

  const selectTaskView = (view: string) => {
    switch (view) {
      case 'list':
        return showTaskList();
      case 'stack':
      default:
        return showTaskStack();
    }
  };

  const showTaskList = async () => {
    if (!isTaskListMode.value) {
      router.push({
        query: {
          ...(route.query || {}),
          task_display_mode: 'list',
        },
      });
    }
  };

  const showTaskStack = async (id?: string) => {
    if (!isTaskStackedMode.value) {
      router.push({
        query: {
          ...(route.query || {}),
          task_display_mode: 'stacked',
        },
      });
    }

    if (id) {
      const task = await peekLoadTask(id);
      if (task) await setCurrentTask(task);

      return currentTask.value;
    }
  };

  const loadCurrentPartner = async (id: string) => {
    if (partners.value[id]) {
      selectedPartnerId.value = id;
      return partners.value[id];
    }

    const partner = await stuut.partners.retrieve(id);

    partners.value[id] = partner;
    selectedPartnerId.value = id;

    return partners.value?.[id];
  };

  const openTaskDrawer = async (args: OpenTaskParams) => {
    showConfetti.value = false;
    setTaskDrawerOpen();

    if (args.task?.data) {
      setCurrentTask(args.task);
    } else if (args.task?.id || args.taskId) {
      try {
        const task = await peekLoadTask(args.taskId || args.task?.id);
        if (task) setCurrentTask(task as Task);
      } catch (err: any) {
        console.error(err.clientMessage);
      }
    }

    if (args.taskView || route.query.task_display_mode) {
      selectTaskView(args.taskView || (route.query.task_display_mode as string));
    } else if (isPartnerGroupView.value) {
      showTaskStack();
    } else if (isTaskTypeGroupView.value) {
      showTaskList();
    }

    return currentTask.value;
  };

  const setTaskDrawerOpen = () => {
    isTaskDrawerOpen.value = true;
  };

  const setTaskDrawerClosed = () => {
    isTaskDrawerOpen.value = false;
  };

  const refreshTaskTable = () => {
    queryClient.invalidateQueries({
      exact: false,
      queryKey: ['tasks'],
    });
  };

  const refreshTaskDrawerTable = () => {
    queryClient.invalidateQueries({
      exact: false,
      queryKey: ['tasksDrawerTable'],
    });
  };

  const refreshTaskGroupByOptions = () => {
    queryClient.invalidateQueries({
      exact: false,
      queryKey: ['tasksByCustomer'],
    });

    queryClient.invalidateQueries({
      exact: false,
      queryKey: ['tasksByTaskType'],
    });
  };

  const refreshAllTaskTables = () => {
    refreshTaskTable();
    refreshTaskDrawerTable();
    refreshTaskGroupByOptions();
  };

  const openTaskByQuery = (task, name?: string) => {
    router.replace({
      name,
      query: {
        collection_id: undefined,
        collection_type: undefined,
        group_view_id: undefined,
        group_view_mode: 'individual',
        page: 0,
        status: ['open'],
        sub_collection_id: undefined,
        sub_collection_type: undefined,
        task_drawer_id: task?.id,
      },
    });
  };

  const hasValidTaskDrawerChanges = (currQPs, oldQPs) => {
    const { list: updatedParamList } = getUpdatedQPs(currQPs, oldQPs);

    return updatedParamList.some((key) =>
      [
        'collection_id',
        'collection_type',
        'group_view_id',
        'sub_collection_id',
        'sub_collection_type',
        'task_drawer_id',
      ].includes(key),
    );
  };

  const getUpdatedQPs = (
    currQPs: any,
    oldQPs: any,
  ): { values: Record<string, any>; list: string[] } => {
    return [
      'group_view_mode',
      'group_view_id',
      'collection_type',
      'collection_id',
      'sub_collection_type',
      'sub_collection_id',
      'task_drawer_id',
      'page',
    ].reduce(
      (acc, curr) => {
        if (currQPs[curr] !== oldQPs[curr]) {
          acc.list.push(curr);
          acc.values[curr] = { new: currQPs[curr], old: oldQPs[curr] };
        }

        return acc;
      },
      { list: [] as string[], values: {} as Record<string, any> },
    );
  };

  // Return all state, actions, and computed properties
  return {
    afterCompleteTask,
    buildCollectionState,
    buildRequestFromQPs,
    // Computed properties
    classifications,
    clearGroupByModeOptions,
    clearValues,
    closeAfterCompleteOverlay,
    closeTaskDrawer,
    collections,
    currentCollectionTasks,
    currentCollectionTasksTotal,
    currentGroupViewMode,
    currentModalClassification,
    currentModalTaskType,
    currentPartner,
    currentTask,
    currentTaskTypeCollections,
    fetchCollectByModeOptions,
    fetchCollections,
    fetchGroupViewModes,
    fetchSubCollections,
    fetchTask,
    getById,
    getIsGroupView,
    getIsIndividualView,

    getTaskParentCollection,
    getTaskParentCollectionType,
    getUpdatedQPs,
    gotoNextTask,
    gotoPreviousTask,
    groupByModeId,
    groupByModeOptions,
    groupTaskCount,
    // State
    groupViewModes,
    handleConfirmAfterComplete,
    hasValidTaskDrawerChanges,
    initializeSharedQueryParams,
    isBasicTask,
    isClassificationCollection,
    isClassificationGroup,
    isFetchingCollectionTasks,
    isGroupView,
    isIndividualTaskView,
    isInitNavLoaded,
    isInitSubNavLoaded,
    isLoadingCollectionTasks,
    isLoadingGroupByModeOptions,
    isLoadingTask,
    isPartnerGroupView,
    isTaskDrawerOpen,
    isTaskListMode,
    isTaskStackedMode,
    isTaskTypeGroupView,
    launchConfetti,
    listPartnerFilter,
    loadCurrentPartner,
    navigationDirection,
    nextCollection,
    nextCollectionIndex,
    nextSubCollection,
    nextSubCollectionIndex,
    nextTask,

    nextTaskIndex,
    openAfterCompleteOverlay,
    openTaskByQuery,

    openTaskDrawer,
    partnerGroupId,
    partners,
    peekFindTask,
    peekLoadTask,
    peekTask,
    previousCollection,
    previousCollectionIndex,
    previousSubCollection,
    previousSubCollectionIndex,
    previousTask,
    previousTaskIndex,
    refreshAllTaskTables,
    refreshTaskDrawerTable,
    refreshTaskTable,
    requestFromQps,
    selectedCollection,
    selectedCollectionId,
    selectedCollectionIndex,
    selectedGroupViewMode,
    selectedGroupViewTypeOption,
    selectedPartnerId,
    selectedSubCollection,
    selectedSubCollectionId,
    selectedSubCollectionIndex,
    selectedTaskId,
    selectedTaskIndex,
    setCollections,
    setCollectionTasks,
    setCurrentTask,
    setGroupViewModeOptions,
    setGroupViewModes,
    setListPartnerFilter,
    setSubCollections,
    setTaskDrawerClosed,
    setTaskDrawerOpen,
    // Actions
    setup,
    sharedTaskQueryParams,
    showAfterCompleteOverlay,
    showConfetti,
    showTaskList,
    showTaskStack,
    subCollections,
    taskDisplayMode,
    toggleTaskView,
    updateFromQps,
    updateSharedQueryParams,
  };
});

export default useTaskCollectionStore;
